/**
 * useQuery hook for the paginated, filtered story list inside a publication
 * (the STORIES tab + section views).
 *
 * Replaces `publicationViewEffects.getStories` + `getAllStoryForPublication`
 * (compose-app). Mirrors the same `POST /api/stories/v1/publications/stories`
 * request + `buildPaginationOutput` shaping.
 *
 * Auth note (§1): stories-service REST uses cookie/session auth via
 * `transport` — NOT bnp-core Auth0 helpers. Filter/section/pagination params
 * are part of the query key, so changing any refetches; TanStack discards
 * results for stale keys (last key wins), replacing the retired effect's
 * manual AbortController de-dupe.
 *
 * Access control (§13): `storiesAccessControlEnabled`, `isAdmin`, and
 * `storyAccess` (story_claims) are forwarded to the server exactly as the
 * retired effect did. `storyAccess` is sourced from the subjectClaims bridge
 * by the caller until that bridge is retired. A 403 response throws
 * `PublicationStoriesForbiddenError` so the caller can navigate to the
 * publication list (the effect did this via `navigateToPublicationList`);
 * navigation is a component concern and stays at the call site.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';

import { getUserById } from '../../../../routes/hasura';
import { buildPaginationOutput } from '../../../../transformations/listTransformation';
import { useToast } from '../../../Toast/context/useToast.context';
import { IGetStoryGraphqlResponse, IStoryListing, IStoryPrivilege } from '../../../../../types';
import { publicationViewQueryKeys } from './publicationViewQueryKeys';

export class PublicationStoriesForbiddenError extends Error {
  constructor() {
    super('Forbidden');
    this.name = 'PublicationStoriesForbiddenError';
  }
}

export type PublicationStoriesKeyParams = {
  publicationId: string;
  entityId: number;
  page: number;
  limit?: number;
  offset?: number;
  sectionId?: string;
  searchText?: string;
  userId?: number;
  userUuid: string;
  entityUuid: string;
  storyState?: string;
  orderByTitle?: string;
  createdAt?: string;
  updatedAt?: string;
  order?: string;
  showAllStoriesForSelectedFolder?: boolean;
  storiesAccessControlEnabled: boolean;
  isAdmin: boolean;
  storyAccess?: IStoryPrivilege[];
};

export type UsePublicationStoriesParams = PublicationStoriesKeyParams | null;

export function usePublicationStories(params: UsePublicationStoriesParams, errorMessage: string) {
  const { toggleToast } = useToast();

  return useQuery<IStoryListing, Error>({
    queryKey: params
      ? publicationViewQueryKeys.stories(params)
      : ([...publicationViewQueryKeys.all, 'stories', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const {
        publicationId,
        limit = 20,
        offset = 0,
        entityId,
        sectionId,
        page,
        searchText,
        userId,
        userUuid,
        entityUuid,
        storyState,
        orderByTitle,
        createdAt,
        updatedAt,
        showAllStoriesForSelectedFolder = false,
        order,
        storiesAccessControlEnabled,
        isAdmin,
        storyAccess = []
      } = params;

      let allStoriesParams = {
        publication_id: publicationId,
        section_id: sectionId,
        entity_id: entityId,
        limit,
        offset,
        user_uuid: userUuid,
        entity_uuid: entityUuid,
        story_claims: storyAccess
      } as Record<string, unknown>;

      if (showAllStoriesForSelectedFolder) {
        delete allStoriesParams.limit;
      }
      if (searchText) {
        allStoriesParams = { ...allStoriesParams, search_text: searchText };
      }
      if (userId) {
        const authorId = await getUserById(userId);
        allStoriesParams = { ...allStoriesParams, user_id: authorId };
      }
      if (storyState && storyState !== 'all') {
        allStoriesParams = { ...allStoriesParams, story_state: storyState };
      }
      if (orderByTitle) {
        allStoriesParams = { ...allStoriesParams, order_by_title: orderByTitle };
      }
      if (createdAt) {
        allStoriesParams = { ...allStoriesParams, created_at: createdAt };
      }
      if (updatedAt) {
        allStoriesParams = { ...allStoriesParams, updated_at: updatedAt };
      }
      if (order) {
        allStoriesParams = { ...allStoriesParams, order: 'asc', order_by_title: 'asc' };
      }
      if (storiesAccessControlEnabled) {
        allStoriesParams = {
          ...allStoriesParams,
          stories_access_control_enabled: storiesAccessControlEnabled,
          is_admin: isAdmin
        };
      }

      // NOTE: the retired effect passed an AbortController to cancel in-flight
      // requests on rapid filter/section changes. TanStack already discards
      // results for non-active query keys (last key wins), so staleness is
      // handled by the key. Abort wiring can be re-added via a per-call
      // AbortController if a network-cancel is required.
      const response = await transport
        .url('/api/stories/v1/publications/stories')
        .json(allStoriesParams)
        .post()
        .json();

      if (response.status === 403) {
        throw new PublicationStoriesForbiddenError();
      }
      if (!response.data) {
        return Promise.reject(response.errors);
      }
      const graphqlResponse: IGetStoryGraphqlResponse[] = response.data.resources;
      const count = response?.data?.count?.aggregate?.count;
      return buildPaginationOutput(page, count, graphqlResponse);
    },
    enabled: params !== null,
    onError: (error) => {
      // Forbidden is handled by the caller (navigation); don't toast it.
      if (error instanceof PublicationStoriesForbiddenError) return;
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
